import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Stack,
  Chip,
  Checkbox,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { payrollService, ShiftSegment } from '../../services/payrollService';

export interface CompletedWorkItem {
  id: number;
  staffId: number;
  staffName: string;
  employeeId: string;
  clientId?: number;
  clientName: string;
  shiftDate: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart: string;
  actualEnd: string;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  hourlyRate: number;
  totalPay: number;
  rateSource?: string;
  segments?: ShiftSegment[];
  isCompleted: boolean;
  isApproved: boolean;
  completedDate?: string;
  payrollGenerated?: boolean;
  payrollId?: number;
}

interface CompletedWorkTableProps {
  data: CompletedWorkItem[];
  selectedWork: number[];
  onSelectWork: (workId: number) => void;
  onSelectAllWork: () => void;
  onGeneratePayroll: () => void;
  onApproveWork: (workId: number) => void;
  onRejectWork: (workId: number) => void;
  onEditShift?: (shift: CompletedWorkItem) => void;
  onApproveShift?: (shift: CompletedWorkItem) => void;
}

export const CompletedWorkTable: React.FC<CompletedWorkTableProps> = ({
  data,
  selectedWork,
  onSelectWork,
  onSelectAllWork,
  onGeneratePayroll,
  onApproveWork,
  onRejectWork,
  onEditShift,
  onApproveShift
}) => {
  return (
    <>
      {data.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedWork.length} of {data.filter(w => !w.payrollGenerated).length} selectable items selected
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onGeneratePayroll}
              disabled={selectedWork.length === 0}
              size="small"
            >
              Generate Payroll for Selected ({selectedWork.length})
            </Button>
          </Stack>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedWork.length === data.filter(w => !w.payrollGenerated).length && data.filter(w => !w.payrollGenerated).length > 0}
                  indeterminate={selectedWork.length > 0 && selectedWork.length < data.filter(w => !w.payrollGenerated).length}
                  onChange={onSelectAllWork}
                />
              </TableCell>
              <TableCell>Staff Member</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Shift Date</TableCell>
              <TableCell>Scheduled Time</TableCell>
              <TableCell>Actual Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>
                <Tooltip title="Base hourly rate from Billing Rates → Staff Rates. Each shift is sliced into StaffPay segments (Weekday Daytime/Evening/Night, Saturday, Sunday) and priced as base rate × segment %. The hourly rate stored on the staff profile is NOT used.">
                  <span>Rate (Billing)</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title="Total pay = sum over each StaffPay segment of (segment hours × base rate × segment percentage).">
                  <span>Total Pay</span>
                </Tooltip>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((work) => (
              <TableRow key={work.id} hover sx={work.payrollGenerated ? { bgcolor: 'action.hover' } : {}}>
                <TableCell padding="checkbox">
                  <Tooltip title={work.payrollGenerated ? 'Payroll already generated for this work' : ''}>
                    <span>
                      <Checkbox
                        checked={selectedWork.includes(work.id)}
                        onChange={() => onSelectWork(work.id)}
                        disabled={work.payrollGenerated}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {work.staffName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {work.employeeId}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {work.clientName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {payrollService.formatDate(work.shiftDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {work.scheduledStart} - {work.scheduledEnd}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {work.actualStart} - {work.actualEnd}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {work.totalHours}h (Actual)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        // Calculate scheduled duration
                        try {
                          const scheduledStart = new Date(`1970-01-01T${work.scheduledStart}`);
                          const scheduledEnd = new Date(`1970-01-01T${work.scheduledEnd}`);
                          const scheduledDuration = (scheduledEnd.getTime() - scheduledStart.getTime()) / (1000 * 60 * 60);
                          return `${scheduledDuration.toFixed(1)}h scheduled`;
                        } catch (error) {
                          return 'Scheduled duration unavailable';
                        }
                      })()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {(() => {
                        // Also calculate actual duration from actual times for comparison
                        try {
                          if (work.actualStart && work.actualEnd) {
                            const actualStart = new Date(`1970-01-01T${work.actualStart}`);
                            const actualEnd = new Date(`1970-01-01T${work.actualEnd}`);
                            const actualDuration = (actualEnd.getTime() - actualStart.getTime()) / (1000 * 60 * 60);
                            return `(${actualDuration.toFixed(1)}h from clock times)`;
                          }
                          return '';
                        } catch (error) {
                          return '';
                        }
                      })()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      priced by Billing-Rate segments
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {(() => {
                    const segs = work.segments ?? [];
                    const hourlySegs = segs.filter(s => s.kind === 'Hourly' || !s.kind);
                    const hasSleepover = segs.some(s => s.kind === 'Sleepover');
                    const hasTravel = segs.some(s => s.kind === 'Travel');
                    const noRateConfigured = work.hourlyRate <= 0 && segs.length === 0;
                    if (noRateConfigured) {
                      return (
                        <Tooltip title={work.rateSource || 'Rate from Billing Rates → Staff Rates'}>
                          <Typography variant="body2" color="error.main">
                            No rate configured
                          </Typography>
                        </Tooltip>
                      );
                    }
                    const hourlyRates = hourlySegs.map(s => s.rate).filter(r => r > 0);
                    const minRate = hourlyRates.length ? Math.min(...hourlyRates) : work.hourlyRate;
                    const maxRate = hourlyRates.length ? Math.max(...hourlyRates) : work.hourlyRate;
                    const summary: string[] = [];
                    if (hasSleepover) summary.push('Sleepover');
                    if (hourlySegs.length > 0) {
                      summary.push(
                        minRate === maxRate
                          ? `${payrollService.formatCurrency(minRate)}/hr`
                          : `${payrollService.formatCurrency(minRate)}–${payrollService.formatCurrency(maxRate)}/hr`
                      );
                    }
                    if (hasTravel) summary.push('+ travel');
                    const tooltipBody = segs.length > 0 ? (
                      <Box sx={{ p: 0.5 }}>
                        {hourlySegs.length > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                            Base {payrollService.formatCurrency(work.hourlyRate)}/hr
                          </Typography>
                        )}
                        {segs.map((s, i) => (
                          <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                            {s.name}: {s.kind === 'Sleepover'
                              ? `1 night × ${payrollService.formatCurrency(s.rate)}`
                              : s.kind === 'Travel'
                                ? `${s.hours.toFixed(2)}km × ${payrollService.formatCurrency(s.rate)}/km`
                                : `${s.hours.toFixed(2)}h × ${payrollService.formatCurrency(s.rate)}/hr`} = {payrollService.formatCurrency(s.amount)}
                          </Typography>
                        ))}
                        {work.rateSource && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75 }}>
                            {work.rateSource}
                          </Typography>
                        )}
                      </Box>
                    ) : (work.rateSource || 'Rate from Billing Rates → Staff Rates');
                    return (
                      <Tooltip title={tooltipBody}>
                        <Box>
                          <Typography variant="body2">
                            {summary.length > 0
                              ? summary[0]
                              : `${payrollService.formatCurrency(work.hourlyRate)}/hr`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {summary.length > 1
                              ? summary.slice(1).join(' · ')
                              : (segs.length > 0
                                ? `${segs.length} segment${segs.length === 1 ? '' : 's'}`
                                : `base ${payrollService.formatCurrency(work.hourlyRate)} · segments apply`)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      (work.segments?.length ?? 0) > 0 ? (
                        <Box sx={{ p: 0.5 }}>
                          {(work.segments ?? []).map((s, i) => (
                            <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                              {s.name}: {s.kind === 'Sleepover'
                                ? `1 night × ${payrollService.formatCurrency(s.rate)}`
                                : s.kind === 'Travel'
                                  ? `${s.hours.toFixed(2)}km × ${payrollService.formatCurrency(s.rate)}/km`
                                  : `${s.hours.toFixed(2)}h × ${payrollService.formatCurrency(s.rate)}/hr`} = {payrollService.formatCurrency(s.amount)}
                            </Typography>
                          ))}
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                            Total: {payrollService.formatCurrency(work.totalPay)}
                          </Typography>
                        </Box>
                      ) : (work.rateSource || 'Pay computed from Billing Rates StaffPay segments')
                    }
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={work.totalPay > 0 ? 'primary' : 'error.main'}
                    >
                      {payrollService.formatCurrency(work.totalPay)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Chip
                      label={work.isCompleted ? "Completed" : "In Progress"}
                      color={work.isCompleted ? "success" : "warning"}
                      size="small"
                    />
                    {work.isApproved ? (
                      <Chip
                        label="Approved"
                        color="info"
                        size="small"
                      />
                    ) : work.isCompleted ? (
                      <Chip
                        label="Pending Approval"
                        color="warning"
                        size="small"
                      />
                    ) : null}
                    {work.payrollGenerated && (
                      <Chip
                        label={work.payrollId ? `Payroll #${work.payrollId}` : 'Payroll Generated'}
                        color="secondary"
                        size="small"
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {work.isCompleted && !work.isApproved ? (
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Approve work">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onApproveWork(work.id)}
                        >
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject work">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onRejectWork(work.id)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : work.isApproved ? (
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Approved
                      </Typography>
                      {onEditShift && (
                        <Tooltip title="Edit shift">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onEditShift(work)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      In Progress
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
